/**
 * Centralized logout handler to prevent race conditions and duplicate logout attempts
 * All components should use this instead of implementing their own logout logic
 */

import { auth } from "../config/firebase";
import { signOut } from "firebase/auth";
import { clearUserUnitCoData } from "../hooks/useUnitJwt";
import { resetUnitCoSession } from "./unitcoReset";
import { trackEvent, clearUserId, AUTH_EVENTS } from "../services/analytics";

// Global flag to prevent multiple simultaneous logout attempts
let isLoggingOut = false;
let logoutPromise: Promise<void> | null = null;
let showModalCallback: ((message?: string) => void) | null = null;

/**
 * Set the callback function to show the session expired modal
 * This should be called once when the app initializes
 */
export const setSessionExpiredModalCallback = (callback: (message?: string) => void) => {
  showModalCallback = callback;
};

/**
 * Centralized logout function - prevents race conditions
 * @param reason - Reason for logout (for logging)
 * @param showModal - Whether to show the session expired modal before logging out (default: true)
 * @param customMessage - Custom message to show in the modal
 */
export const performLogout = async (
  reason: string = "Session expired",
  showModal: boolean = true,
  customMessage?: string
): Promise<void> => {
  // If already logging out, return the existing promise
  if (isLoggingOut && logoutPromise) {
    return logoutPromise;
  }
  
  // If already logged out, return immediately
  if (!auth.currentUser) {
    return;
  }
  
  // Set flag and create logout promise
  isLoggingOut = true;
  
  logoutPromise = (async () => {
    try {
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid;

      // Track logout event before clearing user ID
      trackEvent(AUTH_EVENTS.LOGOUT);
      clearUserId();

      // Show modal first if requested and callback is available
      if (showModal && showModalCallback) {
        const message = customMessage || "Your session has expired for security reasons. Please log in again to continue.";
        showModalCallback(message);

        // Wait a bit for the modal to be visible before proceeding with cleanup
        // The modal will handle the redirect when user clicks the button
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Clear user-specific UnitCo data before clearing all localStorage
      if (userId) {
        clearUserUnitCoData(userId);
      }
      
      // Comprehensive UnitCo session reset
      await resetUnitCoSession();
      
      // Clear all session storages and localStorages BEFORE signOut
      window.sessionStorage.clear();
      window.localStorage.clear();
      
      await signOut(auth);
      
      // Only redirect if modal wasn't shown (modal will handle its own redirect)
      if (!showModal || !showModalCallback) {
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
      }
    } catch (logoutError) {
      console.error("[Logout] Error during logout:", logoutError);
      // Force redirect even if cleanup fails
      window.location.href = "/login";
    } finally {
      // Reset flags after logout completes
      isLoggingOut = false;
      logoutPromise = null;
    }
  })();
  
  return logoutPromise;
};

/**
 * Reset logout state (useful for testing or manual resets)
 */
export const resetLogoutState = (): void => {
  isLoggingOut = false;
  logoutPromise = null;
};
